// Components
import { initDropdowns } from "@components/dropdown";
import { initMobileNav } from "@components/mobile-nav";
import { initMobileSidebar } from "@components/mobile-sidebar";
import { initTabs } from "@components/tabs";
import { initColorPickers } from "@components/colorpicker";
import { initAudioPlayer } from "@components/audio-player";
import { initParticles } from "@components/particles";

// Storage services
import { setElement, removeElement } from "@services/storage/elements";
import {
  setGroupFilenames,
  removeGroupFilenames,
  buildFilePaths,
} from "@services/storage/filenames";
import { initSkinini } from "@services/storage/skinini";
import { downloadSkin, formatBytes } from "@services/download/download";

// Features
import {
  checkElements,
  checkElementButtons,
  initGroupOptions,
} from "@features/element-select";
import { updateProjectStats } from "@features/project/stats";
import { updateProjectName } from "@features/project/name";
import { initProjectOptions } from "@features/project/options";
import { initSkininiOptions } from "@features/skinini/options";
import { initSkinOptions } from "@features/skin/options";
import { initElementOptions } from "@features/element/options";

// Utilities
import { $$, $ } from "@utils/dom";
import { CSS } from "@constants/css-classes";
import { DATA_ATTR } from "@constants/data-attributes";

// Types
import type { ElementSelectData, StoredElement } from "@app-types/index";

/**
 * Handle element selection button click
 * Saves to both elements and filenames localStorage, then redirects
 */
function handleElementSelect(event: MouseEvent): void {
  const button = event.currentTarget as HTMLButtonElement;

  // Disable button immediately to prevent double clicks
  button.disabled = true;

  // Extract data attributes from button
  const groupId = button.dataset.groupId;
  const category = button.dataset.category;

  if (!groupId || !category) {
    button.disabled = false;
    return;
  }

  // Check if this element is already selected (toggle off)
  if (button.dataset.elementSelected) {
    removeElement(groupId);
    removeGroupFilenames(groupId);
    window.location.href = `/${category}`;
    return;
  }

  const data: ElementSelectData = {
    category: category,
    groupName: button.dataset.groupName ?? "",
    elementId: button.dataset.elementId ?? "",
    isHd: (button.dataset.isHd as "0" | "1") ?? "0",
    isAnimated: (button.dataset.isAnimated as "0" | "1") ?? "0",
    isMultiple: (button.dataset.isMultiple as "0" | "1") ?? "0",
    frames: button.dataset.frames ?? "0",
  };

  // Save to elements localStorage
  const elementData: StoredElement = {
    element_id: data.elementId,
    is_hd: data.isHd,
    is_animated: data.isAnimated,
    frames: data.frames,
    group_name: data.groupName,
    category: data.category,
  };
  setElement(groupId, elementData);

  // Get filenames from hidden inputs and save to filenames localStorage
  const filenameInputs = $$<HTMLInputElement>('input[name="filenames[]"]');
  const filenames = filenameInputs.map((input) => input.value);
  const filePaths = buildFilePaths(data, filenames);
  setGroupFilenames(groupId, filePaths);

  // Redirect to category page
  window.location.href = `/${category}`;
}

/**
 * Initialize element selection functionality
 * Attaches click handlers to all buttons with data-element-id
 */
function initElementSelect(): void {
  const elementButtons = $$("[data-element-id]");

  elementButtons.forEach((button) => {
    button.addEventListener("click", handleElementSelect as EventListener);
  });
}

/**
 * Handle download button click
 * Downloads all selected elements as a zip file
 */
async function handleDownload(event: Event): Promise<void> {
  const downloadButton = event.currentTarget as HTMLButtonElement;
  if (!downloadButton) return;

  // Get all placeholder elements (works for both desktop and mobile)
  const placeholders = $$(DATA_ATTR.DOWNLOAD_PLACEHOLDER);
  const placeholderBacks = $$(DATA_ATTR.DOWNLOAD_PLACEHOLDER_BACK);
  const statusSpans = $$(DATA_ATTR.DOWNLOAD_STATUS);
  const downloadButtons = $$<HTMLButtonElement>(DATA_ATTR.PROJECT_DOWNLOAD);
  const logoDownloadings = $$(".logo-downloading");

  /**
   * Show download placeholders and start logo animation
   */
  const showPlaceholders = (): void => {
    placeholders.forEach((el) => {
      el.classList.remove(CSS.HIDDEN);
      el.classList.add(CSS.FLEX);
    });
    placeholderBacks.forEach((el) => {
      el.classList.remove(CSS.HIDDEN);
      el.classList.add(CSS.FLEX);
    });
    logoDownloadings.forEach((el) => {
      el.classList.remove(CSS.LOGO_DOWNLOADING_STOP);
    });
  };

  /**
   * Hide download placeholders
   */
  const hidePlaceholders = (): void => {
    placeholders.forEach((el) => {
      el.classList.add(CSS.HIDDEN);
      el.classList.remove(CSS.FLEX);
    });
    placeholderBacks.forEach((el) => {
      el.classList.add(CSS.HIDDEN);
      el.classList.remove(CSS.FLEX);
    });
  };

  /**
   * Stop logo animation smoothly at the end of current iteration
   */
  const stopLogoAnimation = (): void => {
    logoDownloadings.forEach((logoDownloading) => {
      const svg = $<SVGElement>("svg", logoDownloading);
      if (!svg) {
        logoDownloading.classList.add(CSS.LOGO_DOWNLOADING_STOP);
        return;
      }
      svg.addEventListener(
        "animationiteration",
        () => {
          logoDownloading.classList.add(CSS.LOGO_DOWNLOADING_STOP);
        },
        { once: true },
      );
    });
  };

  /**
   * Update progress display on all status elements
   */
  const updateProgress = (_percent: string, status: string): void => {
    statusSpans.forEach((el) => {
      el.textContent = status;
    });
  };

  /**
   * Set disabled state on all download buttons
   */
  const setButtonsDisabled = (disabled: boolean): void => {
    downloadButtons.forEach((btn) => {
      btn.disabled = disabled;
    });
  };

  try {
    // Disable all buttons and show placeholders
    setButtonsDisabled(true);
    updateProgress("0%", "preparing...");
    showPlaceholders();

    // Download with progress tracking
    await downloadSkin((loaded, total) => {
      if (total > 0) {
        const percent = Math.round((loaded / total) * 100);
        updateProgress(`${percent}%`, formatBytes(loaded));
      } else {
        updateProgress("...", formatBytes(loaded));
      }
    });

    updateProgress("100%", "complete!");
    stopLogoAnimation();

    // Hide placeholders and re-enable buttons after a short delay
    setTimeout(() => {
      hidePlaceholders();
      setButtonsDisabled(false);
    }, 2000);
  } catch (error) {
    console.error("Download failed:", error);
    updateProgress("!", "download failed");
    stopLogoAnimation();

    // Hide placeholders and re-enable buttons after showing error
    setTimeout(() => {
      hidePlaceholders();
      setButtonsDisabled(false);
    }, 3000);
  }
}

/**
 * Initialize download functionality
 * Attaches click handler to all download buttons
 */
function initDownload(): void {
  const downloadButtons = $$(DATA_ATTR.PROJECT_DOWNLOAD);

  downloadButtons.forEach((button) => {
    button.addEventListener("click", handleDownload);
  });
}

// Initialize on DOM ready
document.addEventListener("DOMContentLoaded", () => {
  initDropdowns();
  initMobileNav();
  initMobileSidebar();
  initTabs();
  initAudioPlayer();
  initParticles();
  initSkinini(updateProjectName); // Must run before colorpickers to restore saved values
  updateProjectName(); // Load project name from localStorage
  initColorPickers();
  initElementSelect();
  initGroupOptions();
  initSkininiOptions();
  initProjectOptions();
  initSkinOptions();
  initElementOptions();
  initDownload();
  checkElements();
  checkElementButtons();
  updateProjectStats();
});
